import { format } from 'date-fns';
import { useEffect, useState } from 'react';
import { useGoogleAnalytics } from '../../../../common/hooks/useGoogleAnalytics';
import useTechnicalErrorMessage from '../../../../common/hooks/useTechnicalErrorMessage';
import { subscriptionAPI } from '../../../../../services/redwolfSdkAPI';
import * as Sentry from '@sentry/react';
import Modal from '../../../../common/components/Modal';
import Spinner from '../../../../common/components/Spinner';
import useRequestRedwolfService from '../../../../common/hooks/useRequestRedwolfService';
import { getToken } from '../../../../../utils/localStorage';

import { openNotification } from '../../../../../store/commonSlice';
import { useDispatch } from 'react-redux';

interface SubscriptionUpdateModalProps {
	isAppBundleUpdateModalOpen: boolean;
	isSkeletonLoading: boolean;
	plansList: any;
	subscriptionDetail: any;
	updateAppBundleSubscriptionModalToggle: (
		isAppBundleUpdateSubscriptionModalOpen: boolean,
		emptyPlansList: any,
		reRenderSubscriptionList: any
	) => void;
	updatePurchaseHistory: () => void;
}

const AppBundleUpdateSubscription = ({
	isAppBundleUpdateModalOpen,
	isSkeletonLoading,
	plansList,
	subscriptionDetail,
	updateAppBundleSubscriptionModalToggle,
	updatePurchaseHistory
}: SubscriptionUpdateModalProps) => {
	const [isAppBundleUpdateConfirmModalOpen, setIsAppBundleUpdateConfirmModalOpen] = useState(false);
	const [isSpinnerLoading, setIsSpinnerLoading] = useState(false);
	const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
	const [planDetails, setPlanDetails] = useState<any>({});
	const dispatch = useDispatch();
	const [logGoogleAnalyticsEvent] = useGoogleAnalytics();
	const [showTechnicalErrorMessage] = useTechnicalErrorMessage();
	const [requestRedwolfService] = useRequestRedwolfService();
	const token = getToken();
	const transaction = Sentry.startInactiveSpan({ name: 'Update Subscription' });

	useEffect(() => {
		if (plansList.length) {
			const selectedPlan = plansList.find((plan: any) => plan.selected);
			if (selectedPlan) {
				setPlanDetails(selectedPlan);
			}
		}
	}, [plansList]);

	const handleUpdateSubscriptionAppBundle = (event: any) => {
		event.preventDefault();

		logGoogleAnalyticsEvent('VIZIO_ACCOUNT_WEB_UPDATE_SUBSCRIPTION');
		if (planDetails.planCode === 'cancel') {
			setIsCancelModalOpen(true);
		} else {
			setIsAppBundleUpdateConfirmModalOpen(true);
		}
	};

	const handleUpdateBack = (event: any) => {
		event.preventDefault();
		setIsAppBundleUpdateConfirmModalOpen(false);
	};

	const updateModalClose = () => {
		updateAppBundleSubscriptionModalToggle(false, true, null);
		setPlanDetails({});
	};

	const updateConfirmModalClose = () => {
		setIsAppBundleUpdateConfirmModalOpen(false);
		updateModalClose();
	};

	const handleCancelSubsConfirm = (event: any) => {
		event.preventDefault();
		logGoogleAnalyticsEvent('VIZIO_ACCOUNT_WEB_CANCEL_SUBSCRIPTION');
		setIsSpinnerLoading(true);

		requestRedwolfService(
			subscriptionAPI.cancelSubscription(
				token,
				subscriptionDetail && subscriptionDetail.subscriptionId
			)
		)
			.then((response: any) => {
				if (response.constructor.$metadata$.simpleName === 'Success') {
					logGoogleAnalyticsEvent('VIZIO_ACCOUNT_WEB_CANCEL_SUBSCRIPTION_SUCCESS');
					setIsSpinnerLoading(false);
					updatePurchaseHistory();
					setIsCancelModalOpen(false);
					updateAppBundleSubscriptionModalToggle(false, true, true);
					dispatch(
						openNotification({
							type: 'SUCCESS',
							heading: 'Success',
							body: 'Subscription canceled.'
						})
					);
				} else {
					logGoogleAnalyticsEvent('VIZIO_ACCOUNT_WEB_CANCEL_SUBSCRIPTION_FAILURE', {
						error: 'Unable to process your request. Please try again.'
					});
					setIsSpinnerLoading(false);
					showTechnicalErrorMessage();
				}
				transaction && transaction.end();
			})
			.catch((err: any) => {
				setIsSpinnerLoading(false);
				transaction && transaction.end();
				logGoogleAnalyticsEvent('VIZIO_ACCOUNT_WEB_CANCEL_SUBSCRIPTION_FAILURE', {
					error: 'Unable to process your request. Please try again.'
				});
				showTechnicalErrorMessage();
				Sentry.captureException(err);
			});
	};

	const handleCancelBack = (event: any) => {
		event.preventDefault();
		setIsCancelModalOpen(false);
	};

	const cancelModalClose = () => {
		setIsCancelModalOpen(false);
		updateModalClose();
		setPlanDetails({});
	};

	const basePlanDetailPriceWithAddons = parseFloat(
		subscriptionDetail.plan.price.substring(1).replace(/,/g, '')
	);
	const cancelSubscriptionGrandTotal = subscriptionDetail.total.substring(1).replace(/,/g, '');

	return (
		<>
			{subscriptionDetail &&
				plansList &&
				isAppBundleUpdateModalOpen &&
				!isAppBundleUpdateConfirmModalOpen &&
				!isCancelModalOpen && (
					<Modal
						containerId="updateSubInsideModal"
						headingImg={subscriptionDetail.plan.imageUrl}
						show={isAppBundleUpdateModalOpen}
						onHide={updateModalClose}
						heading="VIZIO Bundles"
						containerClasses="update-subscription cancel-bundles">
						<form className="edit-modal-form fieldn popupForm">
							<div className="plan_details">
								<div className="Plan-content">
									<h3 className="planDetails_heading">Current Plan</h3>
									<p className="planDetails_priceInfo">
										{`${subscriptionDetail.plan.name} `}
										<span className="planDetails_price">
											{`$${basePlanDetailPriceWithAddons.toFixed(2)}/${
												subscriptionDetail.plan.billingInterval
											}`}
										</span>
									</p>

									<p id="current-plan">(Bills {subscriptionDetail.billingDate})</p>
								</div>
							</div>
							<div className="button-field-group">
								<button
									type="submit"
									className="primary-button"
									id="updateSubscriptionButton"
									onClick={handleUpdateSubscriptionAppBundle}>
									Cancel Subscription
								</button>
							</div>
						</form>
					</Modal>
				)}

			{subscriptionDetail && planDetails && isAppBundleUpdateConfirmModalOpen && (
				<Modal
					containerId="updateConfirmSubscription"
					headingImg={subscriptionDetail.plan.imageUrl}
					show={isAppBundleUpdateConfirmModalOpen}
					onHide={updateConfirmModalClose}
					heading="VIZIO Bundles"
					containerClasses="update-subscription cancel-bundles-confirm">
					{isSkeletonLoading ? (
						<Spinner />
					) : (
						<form className="edit-modal-form fieldn popupForm" id="confirmUpdateSubscription">
							<div className="confirm-plan-container">
								<div className="confirm-details-container">
									<div className="confirm-plan-details">
										<div className="new-plan">Selected Plan</div>

										<div className="new-price">
											<p>{`Your subscription will end on ${format(
												new Date(subscriptionDetail.billingDate),
												'MM/dd/yy'
											)}.`}</p>
										</div>
									</div>
								</div>

								{Object.keys(planDetails).length > 0 && planDetails.planCode !== 'cancel' && (
									<div className="addplan-total-amount">
										<div className="bassic-plan-container">
											<p className="addplan-details">
												<span className="baseplan">Base Plan:</span>
												<span className="addon-value">{planDetails.name}</span>
											</p>
											<span className="select-pricedetails">
												{planDetails.price}/{subscriptionDetail.plan.billingInterval}
											</span>
										</div>
									</div>
								)}

								<div className="plan-start-details">
									Subscription Total:
									<span className="due-info">
										{planDetails.price}/{subscriptionDetail.plan.billingInterval}
									</span>
								</div>
							</div>

							<div className="button-field-group confirm-btn m-btn">
								<button type="submit" className="secondary-button" onClick={handleUpdateBack}>
									Back
								</button>

								<button
									type="submit"
									className="primary-button"
									onClick={(event) => handleCancelSubsConfirm(event)}>
									Cancel
								</button>
							</div>
						</form>
					)}
				</Modal>
			)}

			{subscriptionDetail && isCancelModalOpen && (
				<Modal
					containerId="cancelSubscription"
					show={isCancelModalOpen}
					onHide={cancelModalClose}
					heading="Cancel Subscription"
					headingImg={subscriptionDetail.plan.imageUrl}
					containerClasses="update-subscription">
					{isSkeletonLoading ? (
						<Spinner />
					) : (
						<form className="edit-modal-form fieldn popupForm" id="confirmCancelSubscription">
							<div className="confirm-plan-container">
								<div className="new-plan">Selected Plan</div>
								<div className="new-price">
									{`Your subscription will end on ${subscriptionDetail.billingDate}`}.
								</div>
								<div className="addplan-total-amount">
									<div className="bassic-plan-container">
										<p className="addplan-details">
											<span className="baseplan">Base Plan:</span>
											<span className="addon-value">{subscriptionDetail.plan.name}</span>
										</p>
										<span className="select-pricedetails">
											{`${subscriptionDetail.plan.price}/${subscriptionDetail.plan.billingInterval}`}
										</span>
									</div>
								</div>

								<div className="final-total-amount">
									Total:
									<span>{`$${cancelSubscriptionGrandTotal}/${subscriptionDetail.plan.billingInterval}`}</span>
								</div>
							</div>
							<div className="button-field-group m-btn">
								<button
									type="submit"
									className="secondary-button"
									onClick={(event) => handleCancelBack(event)}>
									Back
								</button>
								<button
									type="submit"
									className="primary-button"
									onClick={(event) => handleCancelSubsConfirm(event)}>
									Cancel
								</button>
							</div>
						</form>
					)}
				</Modal>
			)}

			{isSpinnerLoading && <Spinner />}
		</>
	);
};

export default AppBundleUpdateSubscription;
